/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { HiOutlineInbox } from "react-icons/hi";

import ListPage from "../../layout/ListPage";
import type { Column } from "../../ui";
import { useInfoContext } from "../../context/InfoContext";
import { _PAGE_SIZE_ } from "../../lib/other/tools";

type ContactRow = {
  contact_us_id: number | string;
  fullname?: string;
  email_address?: string;
  phone_number?: string;
  subject?: string;
  message?: string;
  createdAt?: string;
  [key: string]: unknown;
};

const Overview: React.FC = () => {
  const { contacts, fetchContacts, count } = useInfoContext() as any;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(_PAGE_SIZE_);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!contacts) fetchContacts(page, pageSize);
  }, [contacts]);

  const rows = useMemo<ContactRow[] | null>(() => {
    if (!Array.isArray(contacts)) return null;
    const q = query.trim().toLowerCase();
    if (!q) return contacts as ContactRow[];
    return (contacts as ContactRow[]).filter((c) =>
      [c.fullname, c.email_address, c.subject, c.message].some((v) =>
        String(v ?? "").toLowerCase().includes(q),
      ),
    );
  }, [contacts, query]);

  const columns: Column<ContactRow>[] = [
    {
      header: "From",
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-gray-900">
            {row.fullname ?? "—"}
          </p>
          <p className="truncate text-[11px] text-gray-500">
            {row.email_address ?? row.phone_number ?? ""}
          </p>
        </div>
      ),
    },
    {
      header: "Message",
      cell: (row) => (
        <div className="min-w-0">
          {row.subject ? (
            <span className="mb-1 inline-flex rounded-full bg-brand/5 px-2 py-0.5 text-[11px] font-medium text-brand ring-1 ring-inset ring-brand/15">
              {row.subject}
            </span>
          ) : null}
          <p className="line-clamp-2 text-gray-700">{row.message ?? ""}</p>
        </div>
      ),
    },
    {
      header: "Sent",
      cell: (row) =>
        row.createdAt ? (
          <span className="text-[11.5px] text-gray-500">
            {new Date(row.createdAt).toDateString()}
          </span>
        ) : (
          <span className="text-gray-300">—</span>
        ),
    },
  ];

  return (
    <ListPage<ContactRow>
      title="Contact Messages"
      subtitle="Inbox of submissions from the public site."
      icon={<HiOutlineInbox className="h-5 w-5" />}
      rows={rows}
      columns={columns}
      rowKey={(r) => r.contact_us_id}
      search={{
        value: query,
        onChange: setQuery,
        placeholder: "Search name, email, or message…",
      }}
      pagination={{
        page,
        pageSize,
        total: count,
        onChange: ({ page: p, pageSize: ps }) => {
          setPage(p);
          setPageSize(ps);
          fetchContacts(p, ps);
        },
      }}
      empty="No contact submissions yet."
    />
  );
};

export default Overview;
